import { useBookingTripLegs } from '@/hooks/useBookingTripLegs';
import { useBookingTrips } from '@/hooks/useBookingTrips';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import type {
  AdmissionDTO,
  BookingReservationDTO,
  LegDTO,
} from '@/types/dto/agent';
import { getBookingAdmissions } from '@/utils/booking';
import { getPlaceIconName, getPtModeIconName } from '@/utils/icon';
import type { CheckoutValues } from '@/utils/zodSchema';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

export interface PassengerSeat {
  firstName: string;
  lastName: string;
  reservedPlace?: {
    coachNumber?: string;
    placeNumber?: Array<string>;
  };
  placeIcon?: 'seat' | 'compartment' | 'bed';
  reservationId?: string;
}

export interface LegInfo {
  id: string;
  origin: string;
  destination: string;
  originStop?: LegDTO['originStop'];
  destinationStop?: LegDTO['destinationStop'];
  legNumber: string;
  direction: 'outboundTrip' | 'returnTrip';
  ptModeIcon: 'train' | 'bus' | 'walk';
  noSeatSelection: boolean;
  passengers: Array<PassengerSeat>;
  ptMode?: LegDTO['ptMode'];
}

export const useSeatSelectionLegs = () => {
  const booking = useCheckoutBooking();
  const { outboundLegs, inboundLegs } = useBookingTripLegs(booking);
  const { outboundTrips, inboundTrips } = useBookingTrips(booking);
  const { watch } = useFormContext<CheckoutValues>();
  const formPassengers = watch('passengers');
  const admissions = getBookingAdmissions(booking);
  const admissionsByLegId = useMemo(
    () =>
      (booking?.bookedTrips ?? []).reduce<
        Record<NonNullable<LegDTO['id']>, Array<AdmissionDTO>>
      >((acc, trip) => {
        (trip.bookedOffers ?? [])
          .flatMap((offer) => offer.admissions || [])
          .forEach((admission) => {
            admission.coveredLegIds?.forEach(
              (legId) => (acc[legId] = [...(acc[legId] ?? []), admission])
            );
          });

        return acc;
      }, {}),
    [booking?.bookedTrips]
  );

  const reservationsWithPassengerIds = useMemo(
    () =>
      admissions.reduce<
        Array<
          BookingReservationDTO & {
            legIds?: Array<string>;
            passengerIds: Array<string>;
          }
        >
      >(
        (acc, { reservations, passengerIds, coveredLegIds }) => [
          ...acc,
          ...(reservations ?? []).map((r) => ({
            ...r,
            passengerIds: passengerIds ?? [],
            legIds: coveredLegIds,
          })),
        ],
        []
      ),
    [admissions]
  );

  const legs = useMemo(() => {
    const allLegs = [...outboundLegs, ...inboundLegs];

    return allLegs
      .map((leg, index) => {
        const legId = leg.id;
        if (!legId) return null;

        const legAdmission = admissionsByLegId[legId];

        const legPassengers =
          booking?.passengers
            ?.map((passenger, passengerIndex) => {
              const formPassenger = formPassengers?.[passengerIndex];

              const legReservation = reservationsWithPassengerIds.find(
                (r) =>
                  r.passengerIds.includes(passenger.id ?? '') &&
                  r.legIds?.includes(legId)
              );

              const hasAdmissionForLeg = legAdmission?.some((admission) =>
                admission.passengerIds?.includes(passenger.id ?? '')
              );

              if (!hasAdmissionForLeg) {
                return null;
              }

              const placeAllocation = legReservation?.placeAllocations;
              const reservedPlace = placeAllocation?.reservedPlaces?.[0];
              const placeIcon = getPlaceIconName(placeAllocation);

              return {
                firstName: formPassenger?.firstName ?? '',
                lastName: formPassenger?.lastName ?? '',
                reservedPlace: reservedPlace?.placeNumber
                  ? {
                      coachNumber: reservedPlace.coachNumber,
                      placeNumber: reservedPlace.placeNumber,
                    }
                  : undefined,
                placeIcon,
                reservationId: legReservation?.id,
              };
            })
            .filter((p): p is NonNullable<typeof p> => p !== null) || [];

        const hasReservedPlace = legPassengers.some(
          (passenger) =>
            Array.isArray(passenger.reservedPlace?.placeNumber) &&
            passenger.reservedPlace.placeNumber.length > 0
        );

        return {
          id: leg.id ?? String(index),
          origin: leg.originStop?.name || '',
          destination: leg.destinationStop?.name || '',
          originStop: leg.originStop,
          destinationStop: leg.destinationStop,
          legNumber: `${index + 1}/${allLegs.length}`,
          direction: outboundLegs.includes(leg)
            ? ('outboundTrip' as const)
            : ('returnTrip' as const),
          ptModeIcon: getPtModeIconName(leg.ptMode),
          noSeatSelection: !hasReservedPlace,
          passengers: legPassengers,
          ptMode: leg.ptMode,
        };
      })
      .filter((leg): leg is NonNullable<typeof leg> => leg !== null);
  }, [
    outboundLegs,
    inboundLegs,
    booking?.passengers,
    formPassengers,
    reservationsWithPassengerIds,
    admissionsByLegId,
  ]);

  return {
    legs,
    outboundTrips,
    inboundTrips,
  };
};
